import React, { memo } from "react";
import { Handle, Position } from "reactflow";

const PALETTE = [
  { border: "#7c3aed", bg: "#f5f3ff", grad: ["#7c3aed","#a78bfa"], text: "#5b21b6" },
  { border: "#0ea5e9", bg: "#f0f9ff", grad: ["#0ea5e9","#38bdf8"], text: "#0369a1" },
  { border: "#059669", bg: "#f0fdf4", grad: ["#059669","#34d399"], text: "#047857" },
  { border: "#d97706", bg: "#fffbeb", grad: ["#d97706","#fbbf24"], text: "#92400e" },
  { border: "#db2777", bg: "#fdf2f8", grad: ["#db2777","#f472b6"], text: "#9d174d" },
  { border: "#0891b2", bg: "#ecfeff", grad: ["#0891b2","#22d3ee"], text: "#0e7490" },
  { border: "#65a30d", bg: "#f7fee7", grad: ["#65a30d","#a3e635"], text: "#3f6212" },
];

function pal(d) { return PALETTE[(d ?? 0) % PALETTE.length]; }

function initials(name = "") {
  return name.split(" ").slice(0, 2).map(w => w[0] || "").join("").toUpperCase() || "?";
}

function fmtNum(v) {
  if (v == null || v === 0) return "₹0";
  if (v >= 1e7) return `₹${(v/1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(v/1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(v/1e3).toFixed(1)}K`;
  return `₹${v}`;
}

const MlmNode = memo(({ data }) => {
  const { member, depth, isSelected, onSelect, onExpand, expanded, loadingChildren } = data;
  const p = pal(depth);
  const hasChildren = (member.total_direct_child ?? 0) > 0;

  return (
    <div
      className="mlm-node-card"
      onClick={() => onSelect && onSelect(member)}
      style={{
        width: 200,
        background: "#fff",
        border: `2px solid ${isSelected ? "#0f172a" : p.border}`,
        borderRadius: 16,
        padding: "13px 13px 10px",
        cursor: "pointer",
        boxShadow: isSelected
          ? `0 0 0 3px ${p.border}50, 0 6px 24px rgba(0,0,0,0.13)`
          : "0 2px 12px rgba(0,0,0,0.08)",
        position: "relative",
        userSelect: "none",
      }}
    >
      <Handle
        type="target" position={Position.Top}
        style={{ width: 8, height: 8, background: p.border, border: "2px solid #fff", top: -5 }}
      />
      {/* Avatar + name */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 11, flexShrink: 0,
          background: `linear-gradient(135deg,${p.grad[0]},${p.grad[1]})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 800, fontSize: 14,
          boxShadow: `0 2px 8px ${p.border}40`,
        }}>
          {initials(member.name)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 12.5, fontWeight: 700, color: "#0f172a",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {member.name}
          </div>
          {member.post_id ? (
            <div style={{
              fontSize: 10, fontWeight: 600, color: p.text,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 1,
            }}>
              {member.post_id[1]}
            </div>
          ) : (
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>No rank</div>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
        background: p.bg, borderRadius: 9,
        padding: "6px 2px", border: `1px solid ${p.border}20`,
      }}>
        <Stat label="Direct" value={member.total_direct_child ?? 0} color={p.text} />
        <Stat label="Own" value={fmtNum(member.total_amount)} color={p.text} divider />
        <Stat label="Team" value={fmtNum(member.child_total_amount)} color={p.text} divider />
      </div>

      {/* Expand / Collapse button */}
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        {hasChildren && (
          <button
            onClick={e => {
              e.stopPropagation();
              onExpand && onExpand(member);
            }}
            style={{
              flex: 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              padding: "5px 0", borderRadius: 8,
              border: `1.5px solid ${p.border}40`,
              background: expanded ? p.bg : `${p.border}10`,
              color: p.border, fontSize: 10, fontWeight: 700,
              cursor: "pointer", fontFamily: "inherit",
              transition: "background 0.15s",
            }}
          >
            {loadingChildren ? <LoadingDot /> : expanded ? "▲ Collapse" : `▼ Show ${member.total_direct_child}`}
          </button>
        )}
        
        <button
          onClick={e => {
            e.stopPropagation();
            // We'll use a standard link or a provided callback to navigate
            window.open(`/tree/${member.id}`, '_blank');
          }}
          style={{
            padding: "5px 10px", borderRadius: 8,
            border: `1.5px solid #e2e8f0`,
            background: "#f8fafc",
            color: "#64748b", fontSize: 10, fontWeight: 700,
            cursor: "pointer", fontFamily: "inherit",
            display: "flex", alignItems: "center", gap: 4,
          }}
          title="Open Subtree in New Tab"
        >
          📂 Open
        </button>
      </div>

      <Handle
        type="source" position={Position.Bottom}
        style={{ width: 8, height: 8, background: p.border, border: "2px solid #fff", bottom: -5 }}
      />
    </div>
  );
});

function Stat({ label, value, color, divider }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      borderLeft: divider ? "1px solid #e2e8f0" : "none",
      padding: "2px 4px",
    }}>
      <span style={{ fontSize: 9, color: "#94a3b8", fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color, marginTop: 1 }}>{value}</span>
    </div>
  );
}

function LoadingDot() {
  return (
    <span style={{
      width: 10, height: 10, borderRadius: "50%",
      border: "1.5px solid currentColor", borderTopColor: "transparent",
      display: "inline-block",
      animation: "spin 0.7s linear infinite",
    }}/>
  );
}

export default MlmNode;
